import { AppRoutes } from '@/app/routes';
import { PERMISSIONS, type Permission } from '@/shared/constants/permissions';

/**
 * Maps a filterset target_model value to its navigable SPA route path.
 * Returns null when there is no dedicated page for the model.
 * This mapping is self-contained and independent of routeUtils / EntityType.
 */
export const getFiltersetTargetModelPath = (targetModel?: string): string | null => {
  if (!targetModel) return null;
  switch (targetModel) {
    // ── Administration ─────────────────────────────────────────────────────
    case 'customer':                     return AppRoutes.admin.clients;
    case 'vehicle':                      return AppRoutes.admin.vehicles;
    case 'driver':                       return AppRoutes.admin.drivers;
    case 'run':                          return AppRoutes.admin.runs;
    case 'run_pool':                     return AppRoutes.admin.runPools;
    case 'company':                      return AppRoutes.admin.providers;
    case 'company_blacklist':            return AppRoutes.admin.providerDenyList;
    case 'fare_billing_code':            return AppRoutes.admin.billing.billingCodes;
    case 'billing_code_set':             return AppRoutes.admin.billing.billingCodeSets;
    case 'funding_source':               return AppRoutes.admin.billing.fundingSources;
    case 'provider_rate':                return AppRoutes.admin.billing.providerRates;
    case 'surcharge':                    return AppRoutes.admin.billing.surcharges;
    case 'purpose':                      return AppRoutes.admin.billing.tripPurpose;
    case 'trip':                         return AppRoutes.trips;
    case 'trip_group':                   return AppRoutes.admin.tripGroups;
    case 'trip_export_profile':          return AppRoutes.tools.tripExportProfiles;
    case 'subscription':                 return AppRoutes.subscriptions;
    case 'template_route':               return AppRoutes.admin.templateRoutes;
    case 'inspection_checklist_template':
    case 'inspection_company_checklist':
    case 'inspection_vehicle_checklist': return AppRoutes.admin.inspections;
    case 'calendar':                     return AppRoutes.admin.calendars;
    case 'client_pool':                  return AppRoutes.admin.centers;
    case 'selfservice_user':             return AppRoutes.admin.selfServiceAccounts;
    case 'driver_addon':                 return AppRoutes.tools.driverAdditionalInfo;
    case 'vehicle_addon':                return AppRoutes.tools.vehicleMaintenance;
    // ── Operations ─────────────────────────────────────────────────────────
    case 'route':                        return AppRoutes.schedules;
    case 'archive_route':                return AppRoutes.archiveSchedules;
    case 'archive_trip':                 return AppRoutes.archiveTrips;
    case 'archive_inspection_checklist': return AppRoutes.operations.inspectionLists;
    case 'archive_inspection_item':      return AppRoutes.operations.archiveInspectionItems;
    case 'reimbursement':                return AppRoutes.reimbursements;
    case 'expense':                      return AppRoutes.expenses;
    case 'alert':                        return AppRoutes.operations.alerts;
    case 'run_status':                   return AppRoutes.operations.runStatus;
    case 'late_trip':                    return AppRoutes.operations.lateTrips;
    case 'on_time_performance':          return AppRoutes.operations.onTimePerformance;
    // ── System ─────────────────────────────────────────────────────────────
    case 'location':                     return AppRoutes.system.locations;
    case 'enclosure':                    return AppRoutes.system.polygons;
    case 'service_area':                 return AppRoutes.system.serviceAreas;
    case 'user':                         return AppRoutes.system.users;
    case 'user_role':                    return AppRoutes.system.userRoles;
    case 'user_filter':                  return AppRoutes.system.filterSet;
    case 'canned_message':               return AppRoutes.system.messageTemplates;
    case 'device':                       return AppRoutes.system.devices;
    case 'mdt_device':                   return AppRoutes.system.mdtDevices;
    case 'gis_address':                  return AppRoutes.system.streetSegments;
    case 'rest_configuration':           return AppRoutes.system.configuration.parameters;
    case 'route_custom_field':           return AppRoutes.system.configuration.scheduleCustomFields;
    case 'report_template':              return AppRoutes.system.configuration.reportTemplates;
    case 'batch_job':                    return AppRoutes.system.configuration.batchJobs;
    case 'allowed_value_set':            return AppRoutes.system.configuration.customValues;
    // ── System Events ──────────────────────────────────────────────────────
    case 'route_event':                  return AppRoutes.system.events.scheduleEvents;
    case 'user_event':                   return AppRoutes.system.events.userEvents;
    case 'trip_event':                   return AppRoutes.system.events.tripEvents;
    case 'subscription_event':           return AppRoutes.system.events.subscriptionEvents;
    case 'batch_job_event':              return AppRoutes.system.events.batchJobEvents;
    case 'reimbursement_event':          return AppRoutes.system.events.reimbursementEvents;
    // ── Tools ──────────────────────────────────────────────────────────────
    case 'feedback':                     return AppRoutes.tools.feedback;
    case 'roadcall':                     return AppRoutes.tools.roadcalls;
    case 'driver_incident':              return AppRoutes.tools.driverIncidents;
    case 'rider_policy_violation':       return AppRoutes.tools.riderPolicyViolations;
    // ── No route (return null) ──────────────────────────────────────────────
    default:                             return null;
  }
};

/**
 * Maps a filterset target_model value to the permission(s) required to view that page.
 * Returns null when no permission gate is needed or the model has no dedicated page.
 * This mapping is self-contained and independent of EntityPermissions.util.
 */
export const getFiltersetTargetModelPermissions = (targetModel?: string): Permission[] | null => {
  if (!targetModel) return null;
  switch (targetModel) {
    case 'customer':                     return [PERMISSIONS.CUSTOMER_VIEW];
    case 'vehicle':                      return [PERMISSIONS.VEHICLE_VIEW];
    case 'driver':                       return [PERMISSIONS.DRIVER_VIEW];
    case 'run':                          return [PERMISSIONS.RUN_VIEW];
    case 'run_pool':                     return [PERMISSIONS.RUN_POOL_VIEW];
    case 'company':                      return [PERMISSIONS.COMPANY_VIEW];
    case 'company_blacklist':            return [PERMISSIONS.COMPANY_BLACKLIST_VIEW];
    case 'fare_billing_code':            return [PERMISSIONS.FARE_BILLING_CODE_VIEW];
    case 'billing_code_set':             return [PERMISSIONS.BILLING_CODE_SET_VIEW];
    case 'funding_source':               return [PERMISSIONS.FUNDING_SOURCE_VIEW];
    case 'provider_rate':                return [PERMISSIONS.PROVIDER_RATE_VIEW];
    case 'surcharge':                    return [PERMISSIONS.SURCHARGE_VIEW];
    case 'purpose':                      return [PERMISSIONS.PURPOSE_VIEW];
    case 'trip':                         return [PERMISSIONS.TRIP_VIEW];
    case 'trip_group':                   return [PERMISSIONS.TRIP_GROUP_VIEW];
    case 'trip_export_profile':          return [PERMISSIONS.TRIP_EXPORT_PROFILE_VIEW];
    case 'subscription':                 return [PERMISSIONS.SUBSCRIPTION_VIEW];
    case 'template_route':               return [PERMISSIONS.TEMPLATE_ROUTE_VIEW];
    case 'inspection_checklist_template':
    case 'inspection_company_checklist':
    case 'inspection_vehicle_checklist': return [PERMISSIONS.INSPECTION_CHECKLIST_TEMPLATE_VIEW];
    case 'calendar':                     return [PERMISSIONS.CALENDAR_VIEW];
    case 'client_pool':                  return [PERMISSIONS.CLIENT_POOL_VIEW];
    case 'selfservice_user':             return [PERMISSIONS.SELFSERVICE_USER_VIEW];
    case 'driver_addon':                 return [PERMISSIONS.DRIVER_ADDON_VIEW];
    case 'vehicle_addon':                return [PERMISSIONS.VEHICLE_ADDON_VIEW];
    case 'route':                        return [PERMISSIONS.METAROUTE_VIEW];
    case 'archive_route':                return [PERMISSIONS.ARCHIVE_ROUTE_VIEW];
    case 'archive_trip':                 return [PERMISSIONS.ARCHIVE_TRIP_VIEW];
    case 'archive_inspection_checklist': return [PERMISSIONS.ARCHIVE_INSPECTION_CHECKLIST_VIEW];
    case 'archive_inspection_item':      return [PERMISSIONS.ARCHIVE_INSPECTION_CHECKLIST_VIEW];
    case 'reimbursement':                return [PERMISSIONS.REIMBURSEMENT_VIEW];
    case 'expense':                      return [PERMISSIONS.EXPENSE_VIEW];
    case 'alert':                        return [PERMISSIONS.ALERT_VIEW];
    case 'run_status':                   return [PERMISSIONS.RUN_VIEW];
    case 'late_trip':                    return [PERMISSIONS.TRIP_VIEW];
    case 'on_time_performance':          return [PERMISSIONS.TRIP_VIEW];
    case 'location':                     return [PERMISSIONS.LOCATION_VIEW];
    case 'enclosure':                    return [PERMISSIONS.ENCLOSURE_VIEW];
    case 'service_area':                 return [PERMISSIONS.SERVICE_AREA_VIEW];
    case 'user':                         return [PERMISSIONS.USER_VIEW];
    case 'user_role':                    return [PERMISSIONS.USER_ROLE_VIEW];
    case 'user_filter':                  return [PERMISSIONS.USER_FILTER_VIEW];
    case 'canned_message':               return [PERMISSIONS.CANNED_MESSAGE_VIEW];
    case 'device':                       return [PERMISSIONS.DEVICE_VIEW];
    case 'mdt_device':                   return [PERMISSIONS.MDT_DEVICE_VIEW];
    case 'gis_address':                  return [PERMISSIONS.GIS_ADDRESS_VIEW];
    case 'rest_configuration':           return [PERMISSIONS.CONFIGURATION_VIEW];
    case 'route_custom_field':           return [PERMISSIONS.METAROUTE_CUSTOM_FIELD_VIEW];
    case 'report_template':              return [PERMISSIONS.REPORT_TEMPLATE_VIEW];
    case 'batch_job':                    return [PERMISSIONS.BATCH_JOB_VIEW];
    case 'allowed_value_set':            return [PERMISSIONS.ALLOWED_VALUE_SET_VIEW];
    case 'route_event':                  return [PERMISSIONS.ROUTE_EVENT_VIEW];
    case 'user_event':                   return [PERMISSIONS.USER_EVENT_VIEW];
    case 'trip_event':                   return [PERMISSIONS.TRIP_EVENT_VIEW];
    case 'subscription_event':           return [PERMISSIONS.SUBSCRIPTION_VIEW];
    case 'batch_job_event':              return [PERMISSIONS.BATCH_JOB_VIEW];
    case 'reimbursement_event':          return [PERMISSIONS.REIMBURSEMENT_VIEW];
    case 'feedback':                     return [PERMISSIONS.FEEDBACK_VIEW];
    case 'roadcall':                     return [PERMISSIONS.ROADCALL_VIEW];
    case 'driver_incident':              return [PERMISSIONS.DRIVER_INCIDENT_VIEW];
    case 'rider_policy_violation':       return [PERMISSIONS.RIDER_POLICY_VIOLATION_VIEW];
    default:                             return null;
  }
};

export const FILTERSET_TARGET_MODEL_MAPPINGS: Record<string, string> = {
  alert: 'Alert',
  allowed_value_set: 'Custom value',
  archive_inspection_checklist: 'Inspection list',
  archive_inspection_item: 'Inspection list item',
  archive_route: 'Archive schedule',
  archive_trip: 'Archive trip',
  archive_vehicle_event: 'Event',
  batch_job: 'Batch job',
  batch_job_event: 'Batch job event',
  billing_code_set: 'Billing code set',
  calendar: 'Calendar',
  canned_message: 'Message template',
  client_pool: 'Center',
  company: 'Provider',
  company_blacklist: 'Provider deny list entry',
  customer: 'Client',
  device: 'Mobility device',
  driver: 'Driver',
  driver_addon: 'Driver additional information',
  driver_incident: 'Driver incident record',
  enclosure: 'Polygon',
  expense: 'Expense',
  fare_billing_code: 'Billing code',
  feedback: 'Feedback record',
  funding_source: 'Funding source',
  gis_address: 'Street segment',
  inspection_checklist_template: 'Inspection form',
  inspection_company_checklist: 'Provider default inspection form',
  inspection_vehicle_checklist: 'Vehicle specific inspection form',
  late_trip: 'Late trip',
  location: 'Location',
  mdt_device: 'MDT device',
  message: 'Message',
  on_time_performance: 'On-time performance',
  provider_rate: 'Provider rate',
  purpose: 'Trip purpose',
  reimbursement: 'Reimbursement',
  reimbursement_event: 'Reimbursement event',
  report_template: 'Report template',
  reservation_main: 'Reservation main',
  rest_configuration: 'Configuration parameter',
  rider_policy_violation: 'Rider policy violation',
  rider_potential_violation: 'Rider potential violation',
  roadcall: 'Road call record',
  route: 'Schedule',
  route_custom_field: 'Schedule custom field',
  route_event: 'Schedule event',
  run: 'Run',
  run_pool: 'Run pool',
  run_status: 'Run status',
  selfservice_user: 'Self-service account',
  service_area: 'Service area',
  subscription: 'Subscription',
  subscription_event: 'Subscription event',
  surcharge: 'Surcharge',
  template_route: 'Fixed route',
  trip: 'Trip',
  trip_event: 'Trip event',
  trip_export_field: 'Field',
  trip_export_profile: 'Trip export profile',
  trip_export_profile_status_rule: 'Conversion rule',
  trip_group: 'Trip group',
  user: 'User',
  user_event: 'User event',
  user_filter: 'Filter set',
  user_preference: 'User preference',
  user_role: 'User role',
  vehicle: 'Vehicle',
  vehicle_addon: 'Vehicle maintenance record',
};

export const getFiltersetTargetModelLabel = (targetModel?: string): string => {
  if (!targetModel) {
    return '';
  }

  return FILTERSET_TARGET_MODEL_MAPPINGS[targetModel] || targetModel;
};
